// Appliquer une vue à des lignes, en mémoire : filtres, tris, regroupement,
// agrégats. Le même code tourne dans la query Convex d'une Database (dans la
// limite de lignes lues de la spec) et côté front pour l'affichage optimiste.
//
// Tout passe par les modules de type (`filters`, `compare`, `groupKeys`,
// `toNumber`) : ce fichier ne connaît aucun type de champ.

import type { AggregateId, FilterCtx, FilterOperator, OperatorId } from "./kind";
import { coerceFieldValue, kindOf, readFieldValue } from "./registry";
import { parseNaturalDate } from "./time";
import type { CoerceCtx, FieldDef, FieldValue, RowMeta } from "./types";

export type ViewRow = {
  id: string;
  values: Record<string, unknown>;
  meta: RowMeta;
};

export type FilterCondition = {
  fieldId: string;
  operator: OperatorId;
  operand?: unknown;
};

export type FilterGroup = {
  conjunction: "and" | "or";
  conditions: (FilterCondition | FilterGroup)[];
};

export type SortSpec = { fieldId: string; direction: "asc" | "desc" };

/** Les conditions d'un groupe de filtres, à plat (groupes imbriqués compris). */
export function conditionsOf(group: FilterGroup): FilterCondition[] {
  return group.conditions.flatMap((condition) =>
    "conjunction" in condition ? conditionsOf(condition) : [condition],
  );
}

/** Le groupe sans les conditions sur les champs écartés ; `undefined` s'il ne reste rien. */
export function withoutConditionsOn(
  group: FilterGroup,
  keep: (fieldId: string) => boolean,
): FilterGroup | undefined {
  const conditions = group.conditions.flatMap((condition): (FilterCondition | FilterGroup)[] => {
    if ("conjunction" in condition) {
      const pruned = withoutConditionsOn(condition, keep);
      return pruned ? [pruned] : [];
    }
    return keep(condition.fieldId) ? [condition] : [];
  });
  return conditions.length > 0 ? { ...group, conditions } : undefined;
}

export type ViewSpec = { filter?: FilterGroup; sorts?: SortSpec[] };

export type QueryCtx = CoerceCtx & FilterCtx;

type AnyOperator = FilterOperator<never, unknown>;

const EMPTY_OPERATORS: AnyOperator[] = [
  { id: "is_empty", operand: "none", matchesEmpty: true, test: () => false },
  { id: "is_not_empty", operand: "none", test: () => true },
];

/** Les opérateurs d'un champ, `is_empty` / `is_not_empty` compris. */
export function operatorsFor(field: FieldDef): AnyOperator[] {
  const own = kindOf(field.type).filters as unknown as AnyOperator[];
  // Une case n'est jamais « vide » : décochée est une réponse.
  return field.type === "checkbox" ? own : [...EMPTY_OPERATORS, ...own];
}

function isGroup(condition: FilterCondition | FilterGroup): condition is FilterGroup {
  return "conjunction" in condition;
}

/** L'opérande brut d'une condition, dans la forme qu'attend l'opérateur. */
function parseOperand(
  field: FieldDef,
  operator: AnyOperator,
  raw: unknown,
  ctx: QueryCtx,
): { ok: true; operand: unknown } | { ok: false; reason: string } {
  switch (operator.operand) {
    case "none":
      return { ok: true, operand: undefined };
    case "text":
      return typeof raw === "string" || typeof raw === "number"
        ? { ok: true, operand: String(raw) }
        : { ok: false, reason: "Expected text." };
    case "day": {
      const parsed = typeof raw === "string" ? parseNaturalDate(raw, ctx) : null;
      return parsed ? { ok: true, operand: parsed.day } : { ok: false, reason: "Expected a date." };
    }
    case "value": {
      const result = coerceFieldValue(field, raw, ctx);
      if (!result.ok) return { ok: false, reason: result.reason };
      if (result.value === null) return { ok: false, reason: "Expected a value." };
      return { ok: true, operand: result.value };
    }
    case "values": {
      // Une valeur par élément, aplaties : « est l'un de » sur un
      // multi_select ou un node prend des ids, pas des listes.
      const items = Array.isArray(raw) ? raw : [raw];
      const values: unknown[] = [];
      for (const item of items) {
        const result = coerceFieldValue(field, item, ctx);
        if (!result.ok) return { ok: false, reason: result.reason };
        if (result.value === null) continue;
        if (Array.isArray(result.value)) values.push(...result.value);
        else values.push(result.value);
      }
      return values.length > 0
        ? { ok: true, operand: values }
        : { ok: false, reason: "Expected at least one value." };
    }
  }
}

type CompiledFilter = (row: ViewRow) => boolean;

export type CompiledView = {
  match: CompiledFilter;
  compare: (a: ViewRow, b: ViewRow) => number;
  // Conditions ignorées (champ supprimé, opérande invalide) : la vue reste
  // utilisable, l'UI les signale.
  errors: { fieldId: string; reason: string }[];
};

/**
 * Prépare une vue une fois pour toutes les lignes : opérandes convertis,
 * opérateurs résolus. Une condition invalide est ignorée et signalée, pas
 * fatale.
 */
export function compileView(
  fields: readonly FieldDef[],
  spec: ViewSpec,
  ctx: QueryCtx,
): CompiledView {
  const byId = new Map(fields.map((field) => [field.id, field]));
  const errors: CompiledView["errors"] = [];
  const cache = new WeakMap<ViewRow, Map<string, FieldValue | null>>();

  const valueOf = (row: ViewRow, field: FieldDef): FieldValue | null => {
    let values = cache.get(row);
    if (!values) {
      values = new Map();
      cache.set(row, values);
    }
    if (!values.has(field.id)) {
      values.set(field.id, readFieldValue(field, row.values[field.id], row.meta));
    }
    return values.get(field.id)!;
  };

  const compileCondition = (condition: FilterCondition | FilterGroup): CompiledFilter | null => {
    if (isGroup(condition)) {
      const parts = condition.conditions
        .map(compileCondition)
        .filter((part): part is CompiledFilter => part !== null);
      if (parts.length === 0) return null;
      return condition.conjunction === "and"
        ? (row) => parts.every((part) => part(row))
        : (row) => parts.some((part) => part(row));
    }
    const field = byId.get(condition.fieldId);
    if (!field) {
      errors.push({ fieldId: condition.fieldId, reason: "Unknown field." });
      return null;
    }
    const operator = operatorsFor(field).find((candidate) => candidate.id === condition.operator);
    if (!operator) {
      errors.push({ fieldId: field.id, reason: `Unsupported operator "${condition.operator}".` });
      return null;
    }
    const operand = parseOperand(field, operator, condition.operand, ctx);
    if (!operand.ok) {
      errors.push({ fieldId: field.id, reason: operand.reason });
      return null;
    }
    const settings = field.settings as never;
    return (row) => {
      const value = valueOf(row, field);
      if (value === null) return operator.matchesEmpty === true;
      return operator.test(value as never, operand.operand, settings, ctx);
    };
  };

  const match = (spec.filter && compileCondition(spec.filter)) ?? (() => true);

  const sorts = (spec.sorts ?? []).flatMap((sort) => {
    const field = byId.get(sort.fieldId);
    if (!field) {
      errors.push({ fieldId: sort.fieldId, reason: "Unknown field." });
      return [];
    }
    return [{ field, sign: sort.direction === "desc" ? -1 : 1 }];
  });

  const compare = (a: ViewRow, b: ViewRow): number => {
    for (const { field, sign } of sorts) {
      const va = valueOf(a, field);
      const vb = valueOf(b, field);
      // Les vides en dernier, dans les deux sens.
      if (va === null || vb === null) {
        if (va === vb) continue;
        return va === null ? 1 : -1;
      }
      const order = kindOf(field.type).compare(va as never, vb as never, field.settings as never);
      if (order !== 0) return order * sign;
    }
    return 0;
  };

  return { match, compare, errors };
}

/** Filtre puis trie. Le tri est stable : à égalité, l'ordre d'entrée (le rang). */
export function applyView(
  rows: readonly ViewRow[],
  fields: readonly FieldDef[],
  spec: ViewSpec,
  ctx: QueryCtx,
): { rows: ViewRow[]; errors: CompiledView["errors"] } {
  const view = compileView(fields, spec, ctx);
  const kept = rows.filter(view.match);
  if (spec.sorts?.length) kept.sort(view.compare);
  return { rows: kept, errors: view.errors };
}

export type RowGroup = { key: string; label: string; rows: ViewRow[] };

/** La clé du groupe des lignes vides (et des options supprimées). */
export const EMPTY_GROUP_KEY = "";

/**
 * Les groupes d'un champ (kanban), dans l'ordre de ses options, le groupe
 * vide en dernier. Une ligne multi_select est dans chacun de ses groupes.
 * `null` si le type ne se regroupe pas.
 */
export function groupRows(rows: readonly ViewRow[], field: FieldDef): RowGroup[] | null {
  const kind = kindOf(field.type);
  if (!kind.groupKeys || !kind.groups) return null;
  const groups = kind.groups(field.settings).map((group) => ({ ...group, rows: [] as ViewRow[] }));
  const byKey = new Map(groups.map((group) => [group.key, group]));
  const empty: RowGroup = { key: EMPTY_GROUP_KEY, label: "", rows: [] };

  for (const row of rows) {
    const value = readFieldValue(field, row.values[field.id], row.meta);
    const keys =
      value === null
        ? kind.emptyGroupKey !== undefined
          ? [kind.emptyGroupKey]
          : []
        : kind.groupKeys(value);
    const targets = keys.map((key) => byKey.get(key)).filter((group) => group !== undefined);
    if (targets.length === 0) empty.rows.push(row);
    for (const group of targets) group.rows.push(row);
  }
  return kind.emptyGroupKey !== undefined ? groups : [...groups, empty];
}

/**
 * La valeur d'un champ de regroupement quand on déplace une carte de la
 * colonne `from` à la colonne `to` d'un kanban (clés de `groupRows`). Une
 * ligne multi_select quitte `from` et rejoint `to`, ses autres valeurs
 * restent ; la colonne vide efface la valeur.
 */
export function valueForGroupMove(
  field: FieldDef,
  current: unknown,
  from: string,
  to: string,
): unknown {
  if (field.type === "checkbox") return to === "true";
  if (field.type === "multi_select") {
    const values = (Array.isArray(current) ? current : []).filter(
      (value): value is string => typeof value === "string" && value !== from,
    );
    if (to !== EMPTY_GROUP_KEY && !values.includes(to)) values.push(to);
    return to === EMPTY_GROUP_KEY ? null : values;
  }
  return to === EMPTY_GROUP_KEY ? null : to;
}

/**
 * Les opérateurs dont l'opérande donne une valeur qui satisfait la
 * condition : « est », « contient », « commence/finit par », les bornes
 * incluses, « est l'un de » (sa première valeur), « contient tout ».
 */
const PREFILL_OPERATORS = new Set<OperatorId>([
  "eq",
  "contains",
  "starts_with",
  "ends_with",
  "gte",
  "lte",
  "on_or_after",
  "on_or_before",
  "is_any_of",
  "has_all_of",
]);

/**
 * Les valeurs d'une ligne neuve qui la font entrer dans une vue filtrée :
 * celles que les conditions du filtre fixent. Seules comptent les conditions
 * d'un groupe « et » de premier niveau : un « ou » laisse le choix. Un champ
 * déjà rempli par une condition garde la première ; une condition dont la
 * valeur ne se convertit pas est ignorée.
 */
export function prefillForFilter(
  fields: readonly FieldDef[],
  filter: FilterGroup | undefined,
  ctx: CoerceCtx,
): Record<string, FieldValue> {
  const values: Record<string, FieldValue> = {};
  if (!filter || filter.conjunction !== "and") return values;
  const byId = new Map(fields.map((field) => [field.id, field]));
  for (const condition of filter.conditions) {
    if (isGroup(condition) || Object.prototype.hasOwnProperty.call(values, condition.fieldId)) continue;
    const field = byId.get(condition.fieldId);
    const raw = prefillOperand(condition);
    if (!field || raw === undefined) continue;
    const result = coerceFieldValue(field, raw, ctx);
    if (result.ok && result.value !== null) values[field.id] = result.value;
  }
  return values;
}

/** La valeur qu'une condition fixe, brute ; `undefined` si elle n'en fixe pas. */
function prefillOperand(condition: FilterCondition): unknown {
  if (condition.operator === "is_checked") return true;
  if (!PREFILL_OPERATORS.has(condition.operator)) return undefined;
  if (condition.operator === "is_any_of" && Array.isArray(condition.operand)) return condition.operand[0];
  return condition.operand;
}

/** Un agrégat de pied de colonne. `null` : rien à calculer (aucune valeur). */
export function aggregate(
  rows: readonly ViewRow[],
  field: FieldDef,
  id: AggregateId,
): number | null {
  const kind = kindOf(field.type);
  if (!kind.aggregates.includes(id)) return null;
  const values = rows.map((row) => readFieldValue(field, row.values[field.id], row.meta));
  const filled = values.filter((value): value is FieldValue => value !== null);
  const total = values.length;
  const percent = (count: number) => (total === 0 ? null : count / total);
  const numbers = () => (kind.toNumber ? filled.map((value) => kind.toNumber!(value)) : []);

  switch (id) {
    case "count":
      return total;
    case "count_empty":
      return total - filled.length;
    case "count_not_empty":
      return filled.length;
    case "count_unique":
      return new Set(filled.map((value) => JSON.stringify(value))).size;
    case "percent_empty":
      return percent(total - filled.length);
    case "percent_not_empty":
      return percent(filled.length);
    case "count_checked":
      return filled.filter((value) => value === true).length;
    case "count_unchecked":
      return total - filled.filter((value) => value === true).length;
    case "percent_checked":
      return percent(filled.filter((value) => value === true).length);
    case "percent_unchecked":
      return percent(total - filled.filter((value) => value === true).length);
    case "sum": {
      const list = numbers();
      return list.length === 0 ? null : list.reduce((sum, n) => sum + n, 0);
    }
    case "average": {
      const list = numbers();
      return list.length === 0 ? null : list.reduce((sum, n) => sum + n, 0) / list.length;
    }
    case "median": {
      const list = numbers().sort((a, b) => a - b);
      if (list.length === 0) return null;
      const middle = Math.floor(list.length / 2);
      return list.length % 2 === 1 ? list[middle]! : (list[middle - 1]! + list[middle]!) / 2;
    }
    case "min":
    case "earliest": {
      const list = numbers();
      return list.length === 0 ? null : Math.min(...list);
    }
    case "max":
    case "latest": {
      const list = numbers();
      return list.length === 0 ? null : Math.max(...list);
    }
    case "range": {
      const list = numbers();
      return list.length === 0 ? null : Math.max(...list) - Math.min(...list);
    }
  }
}
